import React, { useState, useEffect, useMemo } from 'react';
import { FaDatabase, FaEdit, FaTimes } from 'react-icons/fa';
import { Button } from "./custom_ui/button.tsx";
import { update_analytics_data_api } from '../controllers/dataController';
import { useAlert } from '../context/AlertContext';

const SongsTable = ({ songsData, isAdmin = false }) => {
    const { showAlert } = useAlert();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [fullyFilteredSongs, setFullyFilteredSongs] = useState([]);
    const [filteredSongs, setFilteredSongs] = useState([]);
    const [editableSongs, setEditableSongs] = useState([]);
    const [editedRows, setEditedRows] = useState({});
    const [editModeRows, setEditModeRows] = useState({});
    const [originalRowData, setOriginalRowData] = useState({});
    const itemsPerPage = 10;

    const getRowId = (song) => {
        return `${song.month}-${song.song_name}-${song.label}-${song.platform}`;
    };

    useEffect(() => {
        const songsWithIds = (songsData || []).map(song => ({ ...song, id: getRowId(song) }));
        setEditableSongs(songsWithIds);
    }, [songsData]);

    const handleCellChange = (songId, field, value) => {
        const updatedSongs = editableSongs.map(song =>
            song.id === songId ? { ...song, [field]: value } : song
        );
        setEditableSongs(updatedSongs);
        setEditedRows(prev => ({ ...prev, [songId]: true }));
    };

    const handleSave = async (songId) => {
        const songToSave = editableSongs.find(s => s.id === songId);
        const originalSong = originalRowData[songId];

        if (!songToSave || !originalSong) return;

        const payload = {
            ...songToSave,
            original_song_name: originalSong.song_name,
            original_label: originalSong.label,
        };

        try {
            await update_analytics_data_api(payload);
            showAlert('Changes saved successfully!', 'success');
            
            const newId = getRowId(songToSave);

            setEditedRows(prev => {
                const newEditedRows = { ...prev };
                delete newEditedRows[songId];
                return newEditedRows;
            });
            setEditModeRows(prev => {
                const newEditModeRows = { ...prev };
                delete newEditModeRows[songId];
                return newEditModeRows;
            });
             setOriginalRowData(prev => {
                const newOriginalData = { ...prev };
                delete newOriginalData[songId];
                return newOriginalData;
            });

            if (songId !== newId) {
                setEditableSongs(prev => prev.map(s => s.id === songId ? { ...songToSave, id: newId } : s));
            }

        } catch (error) {
            console.error("Failed to save song:", error);
            showAlert(error.message || 'Failed to save changes.', 'danger');
        }
    };

    const handleEditClick = (songId) => {
        const rowData = editableSongs.find(s => s.id === songId);
        setOriginalRowData(prev => ({ ...prev, [songId]: rowData }));
        setEditModeRows(prev => ({ ...prev, [songId]: true }));
    };

    const handleCancelClick = (songId) => {
        // Revert changes from original songsData
        const originalSong = originalRowData[songId];
        if (originalSong) {
            setEditableSongs(prev => prev.map(s => s.id === songId ? originalSong : s));
        }

        // Exit edit mode
        setEditModeRows(prev => {
            const newEditModeRows = { ...prev };
            delete newEditModeRows[songId];
            return newEditModeRows;
        });

        // Discard edit tracking
        setEditedRows(prev => {
            const newEditedRows = { ...prev };
            delete newEditedRows[songId];
            return newEditedRows;
        });

        setOriginalRowData(prev => {
            const newOriginalData = { ...prev };
            delete newOriginalData[songId];
            return newOriginalData;
        });
    };

    const formatMonth = (monthStr) => {
        if (!monthStr) return "";
        const [year, month] = monthStr.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const filteredData = useMemo(() => {
        if (!editableSongs) {
            return [];
        }
        return editableSongs.filter(song => {
            if (!searchTerm) {
                return true;
            }
            const term = searchTerm.toLowerCase();
            const formattedMonth = formatMonth(song.month).toLowerCase();
            
            return (
                song.song_name?.toLowerCase().includes(term) ||
                song.label?.toLowerCase().includes(term) ||
                song.platform?.toLowerCase().includes(term) ||
                formattedMonth.includes(term)
            );
        });
    }, [searchTerm, editableSongs]);

    useEffect(() => {
        setFullyFilteredSongs(filteredData);
    }, [filteredData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setFilteredSongs(fullyFilteredSongs.slice(startIndex, endIndex));
    }, [currentPage, fullyFilteredSongs]);

    return (
        <div className="bg-gradient-card border border-border/50 rounded-md p-4 mt-8">
            <h3 className="text-lg font-semibold mb-4">
                <FaDatabase className="inline-block mr-2 text-green-500" /> Songs Data
            </h3>
            <div className="flex justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center">
                    <input
                        type="text"
                        placeholder="Search songs, labels, platforms..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-gray-700 text-white rounded-md p-2 w-64 md:w-80"
                    />
                </div>

                <div className="flex items-center">
                    <p className='pe-2 text-sm'>Total Pages: {Math.ceil(fullyFilteredSongs.length / itemsPerPage)} </p>
                    <Button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="bg-gray-700 text-white rounded-md p-2 mr-2"
                    >
                        Previous
                    </Button>
                    <span className="text-sm">Page {currentPage}</span>
                    <Button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={filteredSongs.length < itemsPerPage || currentPage * itemsPerPage >= fullyFilteredSongs.length}
                        className="bg-gray-700 text-white rounded-md p-2 ml-2"
                    >
                        Next
                    </Button>
                </div>
            </div>

            <div className='w-full'>
                <div className='w-full overflow-x-auto'>
                    <table className='w-full select-none scroll-smooth text-sm text-left'>
                        <thead className='bg-gradient-card/10 border border-border'>
                            <tr>
                                <th className='p-4'>Month</th>
                                <th className='p-4'>Song Name</th>
                                <th className='p-4'>Label</th>
                                <th className='p-4'>Platform</th>
                                <th className='p-4'>Total Views</th>
                                <th className='p-4'>Total Royalty</th>
                                {isAdmin && <th className='p-4'>Actions</th>}
                            </tr>
                        </thead>
                        <tbody className='tbody' key={searchTerm + currentPage}>
                            {filteredSongs.length > 0 ? (
                                filteredSongs.map((song, index) => {
                                    const isEditing = editModeRows[song.id];
                                    return (
                                        <tr 
                                            className={`border-b border-gray-700 animate-in fade-in slide-in-from-bottom-2 duration-500 ${isEditing ? 'bg-primary/10' : ''}`}
                                            style={{ animationDelay: `${index * 50}ms` }}
                                            key={song.id || index}
                                        >
                                            <td className='p-4'>{formatMonth(song.month)}</td>
                                            <td className='p-4 max-w-xs truncate'>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        value={song.song_name}
                                                        onChange={(e) => handleCellChange(song.id, 'song_name', e.target.value)}
                                                        className="bg-gray-800 text-white rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                ) : (
                                                    <span title={song.song_name}>{song.song_name}</span>
                                                )}
                                            </td>
                                            <td className='p-4'>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        value={song.label}
                                                        onChange={(e) => handleCellChange(song.id, 'label', e.target.value)}
                                                        className="bg-gray-800 text-white rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                ) : (
                                                    song.label
                                                )}
                                            </td>
                                            <td className='p-4'>{song.platform}</td>
                                            <td className='p-4'>{song.total_views}</td>
                                            <td className='p-4'>₹{(song.total_royalty || 0).toFixed(2)}</td>
                                            {isAdmin && (
                                                <td className='p-4'>
                                                    <div className="flex items-center gap-2">
                                                        {isEditing ? (
                                                            <>
                                                                <Button
                                                                    onClick={() => handleSave(song.id)}
                                                                    disabled={!editedRows[song.id]}
                                                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-xs"
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon" 
                                                                    onClick={() => handleCancelClick(song.id)}
                                                                    className="text-muted-foreground hover:text-destructive"
                                                                >
                                                                    <FaTimes size={14} />
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                onClick={() => handleEditClick(song.id)}
                                                                className="text-muted-foreground hover:text-primary"
                                                            >
                                                                <FaEdit size={14} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr className="animate-in fade-in zoom-in duration-300">
                                    <td colSpan={isAdmin ? 7 : 6} className="text-center p-4">No songs found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SongsTable;
